import { ExerciseNavigationHeader } from './ExerciseNavigationHeader';
import { HearingExerciseText } from '@/components/HearingExercise/text';
import { ReadTextExercise } from '@/components/ReadTextExercise';
import { SprechenTextExercise } from '@/components/SpeakingExercise/text';
import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveWidth,
} from '@/components/Util/reactResponsiveDimensions';

import { WritingTextExercise } from '@/components/WritingExercise/text';
import theme from '@/config/theme';
import { isPatientApp } from '@/config/constants';
import { AudioContextProvider } from '@/contexts/AudioContext';
import { useAufgabenExerciseText } from '@/hooks/useAufgabenExerciseText';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { useNavigation, useRoute } from '@react-navigation/native';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { styles } from './text.styles';
import { Bar } from 'react-native-progress';
import { SafeAreaView } from 'react-native-safe-area-context';
import { TASKS } from './constants';
import { allowNativeDriver } from '@/utils/allowNativeDriver';
import { useExerciseInteractionPanResponder } from '@/hooks/useExerciseInteractionPanResponder';

function Exercises() {
  const starIconSize = useResponsiveFontSize(2);
  const { params } = useRoute();

  const {
    setId,
    patientId,
    localWordset,
    isSelf,
    selectedPatient,
    activeSets,
    activatedExerciseModalities,
    preview,
  } = params ?? {
    setId: '',
    patientId: '',
    setType: '',
    localWordset: {},
    isSelf: false,
    selectedPatient: null,
    activeSets: [],
    preview: false,
  };

  const goHome = () => {
    if (isPatientApp) {
      navigate('Home');
    } else {
      navigate('SelfTraining', {
        selectedPatient,
        activeSets,
        preview,
      });
    }
  };

  const {
    state,
    scope,
    PAGER_OPTIONS,
    back,
    showQuestions,
    skip,
    touched, //?  useTimeService return value
    goSelect,
    checkProgress,
  } = useAufgabenExerciseText(
    setId,
    localWordset,
    patientId,
    goHome,
    isSelf,
    activatedExerciseModalities,
    preview,
  );

  const { panResponder, isTouched } = useExerciseInteractionPanResponder({
    isSelf,
    preview,
    touched,
  });

  const dailyGoalActivated = state.settings?.activateDailyGoal;

  const circleSize = useResponsiveWidth(1);
  const progressBarHeight = useResponsiveHeight(2);
  const circleStyle = {
    height: circleSize,
    width: circleSize,
    borderRadius: 50,
    backgroundColor: '#808080',
    marginHorizontal: 1,
  };
  const progressBarStyle = { height: progressBarHeight, width: '50%' };

  const { goBack, navigate } = useNavigation();

  const activeExercise = scope?.exercise;
  const activeExerciseName = activeExercise?.name;
  const settings = state?.settings?.[activeExerciseName];

  const progressBarValue = useMemo(() => {
    if (state.exercises && state.exercises.length > 0) {
      return (state.currentExercise + 1) / state.exercises.length;
    } else {
      return 0;
    }
  }, [state.currentExercise, state.exercises, state.exercises.length]);

  const exerciseClassIcon = useMemo(() => {
    return (
      TASKS.text.find(
        (task) => task.exerciseSettingsKey == activeExerciseName,
      ) ?? {
        label: '',
        iconName: '',
        iconLibrary: '',
        exerciseSettingsKey: '',
      }
    );
  }, [activeExerciseName]);

  const PAGER_RENDERER = useMemo(() => {
    return PAGER_OPTIONS.map((option) => {
      const isActive = option == activeExercise?.id;
      return (
        <View
          style={[circleStyle, isActive && styles.activeCircle]}
          key={option}
        />
      );
    });
  }, [JSON.stringify(PAGER_OPTIONS), activeExercise?.id]);

  const handlePressSkip = () => {
    skip();
  };

  const handlePressBack = useCallback(() => {
    if (isSelf) {
      if (isTouched.current) {
        goSelect();
      } else {
        goHome();
      }
    } else {
      goBack();
    }
  }, [goSelect]);

  let iterationKey = activeExercise
    ? activeExercise.uniqueExercisePrepareDate
    : null;

  const ActiveComponent = {
    Verstehen: (
      <HearingExerciseText
        id={iterationKey}
        checkProgress={checkProgress}
        showQuestions={showQuestions}
        back={back}
        skip={handlePressSkip}
        exercise={cloneDeep(activeExercise)}
        settings={settings}
        exerciseState={scope?.exerciseState}
        audioUrl={scope?.audioUrl}
        currentExercise={state?.currentExercise}
      />
    ),
    Lesen: (
      <ReadTextExercise
        checkProgress={checkProgress}
        showQuestions={showQuestions}
        back={back}
        skip={handlePressSkip}
        exercise={cloneDeep(activeExercise)}
        settings={settings}
        exerciseState={scope?.exerciseState}
        currentExercise={state?.currentExercise}
      />
    ),
    Sprechen: (
      <SprechenTextExercise
        id={iterationKey}
        checkProgress={checkProgress}
        audioUrl={scope?.exercise?.textNormalAudioURL}
        showQuestions={showQuestions}
        back={back}
        skip={handlePressSkip}
        exercise={cloneDeep(activeExercise)}
        settings={settings}
        exerciseState={scope?.exerciseState}
        currentExercise={state?.currentExercise}
      />
    ),
    Schreiben: (
      <WritingTextExercise
        checkProgress={checkProgress}
        skip={handlePressSkip}
        exercise={cloneDeep(activeExercise)}
        settings={settings}
        currentExercise={state?.currentExercise}
        isSelf={isSelf}
      />
    ),
  };

  return (
    <>
      <ExerciseNavigationHeader
        isSelf={isSelf}
        exerciseClass={exerciseClassIcon}
        onPressHome={handlePressBack}
        onPressSkip={handlePressSkip}
        activeExerciseClass="text"
        skipTitle="Überspringen"
      />
      <View style={styles.container}>
        <SafeAreaView style={styles.body} {...panResponder.panHandlers}>
          {scope?.exercise ? (
            <View style={styles.body} key={iterationKey}>
              {ActiveComponent[activeExerciseName]}
            </View>
          ) : null}
          {!isSelf ? (
            <View style={[styles.row, styles.pager]}>{PAGER_RENDERER}</View>
          ) : (
            <View style={[styles.row, styles.pager]}>
              {dailyGoalActivated ? (
                <>
                  <Bar
                    width={null}
                    borderRadius={8}
                    borderWidth={0}
                    height={24}
                    key="progress"
                    color={theme.colors.primary}
                    unfilledColor="rgba(81,191,146,0.3)"
                    progress={state.currentProgress}
                    style={progressBarStyle}
                    useNativeDriver={allowNativeDriver()}
                  />
                  <FontAwesome
                    name="star"
                    size={starIconSize}
                    color={theme.colors.gold}
                    solid
                  />
                </>
              ) : (
                <View>
                  <Bar
                    width={null}
                    height={24}
                    key="progress"
                    borderRadius={8}
                    borderWidth={0}
                    color={theme.colors.primary}
                    progress={
                      progressBarValue == Infinity ? 0 : progressBarValue
                    }
                    style={progressBarStyle}
                    useNativeDriver={allowNativeDriver()}
                  />
                </View>
              )}
            </View>
          )}
        </SafeAreaView>
      </View>
    </>
  );
}

export function AufgabenExerciseText(props) {
  return (
    <AudioContextProvider>
      <Exercises {...props} />
    </AudioContextProvider>
  );
}
